import { useMemo } from "react";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import { usePagination } from "~/hooks/use-pagination";
import { hrefWithQuery } from "~/router-search";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationHrefLink,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";

const sizes = [10, 20, 30, 40, 50];

type PageSlot = { kind: "page"; value: number } | { kind: "ellipsis" };

function buildPaginationPageSlots(page: number, pageCount: number): PageSlot[] {
  if (pageCount <= 5) {
    return Array.from({ length: pageCount }, (_, index) => ({
      kind: "page" as const,
      value: index,
    }));
  }

  if (page <= 3) {
    return [
      { kind: "page", value: 0 },
      { kind: "page", value: 1 },
      { kind: "page", value: 2 },
      { kind: "ellipsis" },
      { kind: "page", value: pageCount - 1 },
    ];
  }

  if (page >= pageCount - 2) {
    return [
      { kind: "page", value: 0 },
      { kind: "ellipsis" },
      { kind: "page", value: pageCount - 3 },
      { kind: "page", value: pageCount - 2 },
      { kind: "page", value: pageCount - 1 },
    ];
  }

  return [
    { kind: "page", value: 0 },
    { kind: "ellipsis" },
    { kind: "page", value: page - 1 },
    { kind: "ellipsis" },
    { kind: "page", value: pageCount - 1 },
  ];
}

function paginationSlotMinWidth(pageCount: number): string {
  if (pageCount >= 1000) {
    return "min-w-12";
  }
  if (pageCount >= 100) {
    return "min-w-11";
  }
  if (pageCount >= 10) {
    return "min-w-10";
  }
  return "min-w-9";
}

export const PaginationController = ({
  itemsCount,
  pageSizeLabel,
  selectAllLabel,
  onSelectAll,
  defaultPage = 1,
  defaultPageSize = 10,
  prefix,
}: {
  itemsCount: number;
  pageSizeLabel?: string;
  selectAllLabel?: string;
  onSelectAll?: () => void;
  defaultPage?: number;
  defaultPageSize?: number;
  prefix?: string;
}) => {
  const { t } = useTranslation("general");
  const { pathname } = useLocation();
  const { link, page, pageSize } = usePagination({
    defaultPage,
    defaultPageSize,
    prefix,
  });
  const initialPageIndex = 1;
  const pageCount = Math.ceil(itemsCount / pageSize);

  const pageSlots = useMemo(
    () => buildPaginationPageSlots(page, pageCount),
    [page, pageCount],
  );
  const toHref = (query: string) => hrefWithQuery(pathname, query);
  const slotClassName = `flex items-center justify-center ${paginationSlotMinWidth(pageCount)}`;

  const pageNumberContent = pageSlots.map((slot, index) => {
    const key =
      slot.kind === "ellipsis"
        ? `ellipsis-${index}`
        : `page-${slot.value}-${index}`;

    if (slot.kind === "ellipsis") {
      return (
        <span key={key} className={slotClassName}>
          <PaginationEllipsis label="..." />
        </span>
      );
    }

    return (
      <span key={key} className={slotClassName}>
        <PaginationLink
          href={toHref(link({ page: slot.value + initialPageIndex }))}
          isActive={page === slot.value + initialPageIndex}
        >
          {String(slot.value + 1)}
        </PaginationLink>
      </span>
    );
  });

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex items-center space-x-2">
        <Pagination className="w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href={toHref(link({ page: page - 1 }))}
                disabled={page === initialPageIndex}
                label={t("buttons.prev")}
              />
            </PaginationItem>
            {pageCount > 5 ? (
              <PaginationItem>
                <div
                  className="flex justify-start gap-1"
                  data-slot="pagination-page-numbers"
                >
                  {pageNumberContent}
                </div>
              </PaginationItem>
            ) : (
              pageNumberContent.map((pageNumberNode) => (
                <PaginationItem key={pageNumberNode.key}>
                  {pageNumberNode}
                </PaginationItem>
              ))
            )}
            <PaginationItem>
              <PaginationNext
                href={toHref(link({ page: page + 1 }))}
                disabled={
                  page - initialPageIndex === pageCount - 1 || pageCount === 0
                }
                label={t("buttons.next")}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        {selectAllLabel && (
          <button
            className="text-primary-500 underline ml-4"
            onClick={onSelectAll}
            type="submit"
          >
            {selectAllLabel}
          </button>
        )}
      </div>

      {pageSizeLabel && (
        <div className="text-secondary-600 flex items-center">
          <Text as="div" variant="body" className="mr-2">
            {pageSizeLabel}
          </Text>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="tertiary"
                label={String(pageSize)}
                icon="chevron-down"
                size="small"
                className="text-sm"
                iconPosition="right"
                aria-label="Page size"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="min-w-20">
              {sizes.map((value) => (
                <DropdownMenuItem key={value} asChild>
                  <PaginationHrefLink
                    href={toHref(link({ pageSize: value, page: defaultPage }))}
                  >
                    {value}
                  </PaginationHrefLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};
